import React, { useEffect, useState } from 'react';
import {
    Paper, Typography, Button, Stack, Chip, Alert, AlertTitle, CircularProgress, Box,
    Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import { Refresh, SignalCellularAlt } from '@mui/icons-material';
import PageHelp from '../../components/common/PageHelp';
import {
    getReachTruth, ReachTruth, ReachSegmentKey, HealthProblemKey, RequestOutcomes,
} from '../../services/cityOperationService';
import { opsErrorMessage } from './opsErrors';

// T4 (driver reach, 2026-10-07): what the nightly snapshot knows about reaching drivers — read-only.

export const SEGMENTS: Array<{ key: ReachSegmentKey; label: string; means: (days: number) => string; color: string }> = [
    { key: 'reachable', label: 'Reachable', means: (d) => `App alive in the last ${d} days (push receipt or location).`, color: '#2e7d32' },
    { key: 'push_dark', label: 'Not answering', means: () => 'We sent something this app build answers automatically (a silent location check, or any push once receipts are on) and it never answered. Invites never count.', color: '#d32f2f' },
    { key: 'unproven', label: 'Not yet proven', means: (d) => `The app can answer, but we have not asked it in the last ${d} days (or asked under an hour ago).`, color: '#757575' },
    { key: 'old_app', label: 'Old app', means: (d) => `Silent for ${d}+ days on an app build that can neither confirm a push nor answer a location ping.`, color: '#ed6c02' },
    { key: 'uninstalled', label: 'Uninstalled', means: () => 'Firebase told us their push token is dead.', color: '#d32f2f' },
    { key: 'never_token', label: 'Never opened the app', means: () => 'No sign the app was ever installed: no push token, no app version and no location ever reported.', color: '#d32f2f' },
    { key: 'no_token', label: 'No push token', means: () => 'Had the app once (a token, app version or location was seen) but has no push token now, with no recorded uninstall.', color: '#ed6c02' },
];

export const HEALTH_PROBLEMS: Array<{ key: HealthProblemKey; label: string; means: string }> = [
    { key: 'notificationsOff', label: 'Notifications off', means: 'The phone blocks every notification from the app — no ride alerts at all.' },
    { key: 'rideChannelBlocked', label: 'Ride alerts muted', means: 'The ride-alert channel is switched off, so ride offers arrive silently or not at all.' },
    { key: 'batteryOptimized', label: 'Battery saver on the app', means: 'Android may stop the app in the background; alerts arrive late or never.' },
    { key: 'bgLocationNotAlways', label: 'Location not "Allow all the time"', means: 'Location stops when the app is in the background, so we lose where they are.' },
    { key: 'overlayOff', label: 'Display over other apps off', means: 'The ride card cannot pop up on top of other apps.' },
    { key: 'fullScreenOff', label: 'Full-screen alerts off', means: 'A locked phone cannot light up with the ride card (Android 14+).' },
    { key: 'restrictedStandby', label: 'Restricted by Android', means: 'Android put the app in a rare/restricted bucket; pushes are delayed or dropped.' },
    { key: 'backgroundRestricted', label: 'Background use blocked', means: 'The phone forbids the app from running in the background, so alerts and location stop when it is closed.' },
];

/** Bar / table parts, bottom to top. Older snapshots lack the split: every cancel counts as late, nothing as unrecorded. */
export type OutcomePart = 'accepted' | 'expired' | 'cancelledLate' | 'cancelledEarly' | 'unrecorded' | 'pending';
export const OUTCOME_PARTS: OutcomePart[] = ['accepted', 'expired', 'cancelledLate', 'cancelledEarly', 'unrecorded', 'pending'];
export const OUTCOME_COLORS: Record<OutcomePart, string> = {
    accepted: '#2e7d32', expired: '#d32f2f', cancelledLate: '#ed6c02', cancelledEarly: '#90a4ae', unrecorded: '#6a1b9a', pending: '#bdbdbd',
};
export const outcomePart = (c: RequestOutcomes, k: OutcomePart): number => {
    if (k === 'cancelledLate') return c.cancelledLate ?? c.cancelled;
    if (k === 'cancelledEarly') return c.cancelledEarly ?? 0;
    if (k === 'unrecorded') return c.unrecorded ?? 0;
    return c[k];
};
export const outcomeLabel = (k: OutcomePart, sec = 60): string => ({
    accepted: 'accepted',
    expired: 'expired unaccepted',
    cancelledLate: `cancelled after ${sec} s of searching`,
    cancelledEarly: `cancelled within ${sec} s (rider changed mind)`,
    unrecorded: 'outcome not recorded',
    pending: 'still searching',
}[k]);
/** Unfilled = expired + cancelled after the first minute; a quick cancel is the rider's choice, not missing supply. */
export const unfilledOf = (c: RequestOutcomes) => c.expired + outcomePart(c, 'cancelledLate');

/** "↑3" / "↓2" / "±0"; empty when there is no week-ago snapshot. */
export const fmtSegmentDelta = (d: number | undefined): string => {
    if (d == null) return '';
    if (d === 0) return '±0';
    return d > 0 ? `↑${d}` : `↓${Math.abs(d)}`;
};

/** Snapshot time in IST, e.g. "8 Oct 2026, 04:00 IST". */
export const fmtSnapshotTime = (iso?: string | null): string | null => {
    if (!iso) return null;
    const t = new Date(iso);
    if (isNaN(t.getTime())) return null;
    return `${t.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' })} IST`;
};

const pct = (n: number, of: number) => (of > 0 ? `${Math.round((n / of) * 100)}%` : '—');
const hourLabel = (h: number) => `${String(h).padStart(2, '0')}:00`;
const weekLabel = (iso: string) =>
    new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' });

interface Props {
    /** City picked on City Operations; empty = fleet-wide. */
    city?: string;
    launchCities?: string[];
}

const ReachTruthPanel: React.FC<Props> = ({ city, launchCities = [] }) => {
    const [truth, setTruth] = useState<ReachTruth | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);

    const load = async (fresh = false) => {
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            const res = await getReachTruth(city || undefined, fresh);
            setTruth(res.data);
        } catch (e: any) {
            if (e?.response?.status === 429) setNotice(e?.response?.data?.message || 'Recomputed recently. Try again in a few minutes.');
            else setError(opsErrorMessage(e, 'Could not load reach truth.'));
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => { load(); }, [city]); // eslint-disable-line react-hooks/exhaustive-deps

    const scope = city || 'Fleet-wide';
    const d = truth?.data ?? null;
    const days = d?.config.activeDays ?? 7;
    const trend = truth?.trend ?? null;
    const trendSkipped = truth?.trendSkipped ?? null;
    const asOfLabel = fmtSnapshotTime(d?.asOf) ?? truth?.asOf ?? null;
    const sec = d?.unfilled.cancelEarlySec ?? 60;
    const hasSplit = d?.unfilled.cancelEarlySec != null;
    const maxHour = d ? Math.max(1, ...d.unfilled.byHour.map((h) => h.requests)) : 1;
    const maxWeek = d ? Math.max(1, ...d.uninstallsByWeek.map((w) => w.count)) : 1;
    const measured = d?.health.measured;
    const problems = d ? HEALTH_PROBLEMS.map((p) => ({
        ...p, count: d.health.problems[p.key] ?? 0, notMeasured: !!measured && (measured[p.key] ?? 0) === 0,
    })) : [];
    const t = d?.unfilled.totals;
    const trendLine = trend
        ? ` · change vs ${trend.since}`
        : trendSkipped
            ? ` · rules changed since ${trendSkipped.since}, no comparison`
            : ' · week-over-week change appears after 7 days of snapshots';

    return (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="h6" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SignalCellularAlt /> Reach truth — can we actually reach our drivers?
                </Typography>
                <PageHelp k="cityOpsReachTruth" />
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Built every night at 04:00 IST from push receipts, locations, phone-health reports and dispatch logs.
                A stored push token is not proof the app is installed — these numbers separate the two.
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Chip size="small" label={scope} color={city ? 'primary' : 'default'} variant="outlined" />
                {truth?.source === 'snapshot' && asOfLabel && <Chip size="small" variant="outlined" label={`As of ${asOfLabel}`} />}
                <Button
                    variant="text" size="small" onClick={() => load(true)} disabled={busy}
                    startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <Refresh />}
                >
                    Recompute now
                </Button>
            </Stack>

            {notice && <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>{notice}</Alert>}
            {error && <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>{error}</Alert>}

            {truth && !d && (
                <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
                    <AlertTitle>No reach-truth snapshot yet for {scope}</AlertTitle>
                    Turn on REACH_SEGMENTS_ENABLED; the first snapshot is written at 04:00 IST.
                    {city && !launchCities.includes(city) && ' This city is not in Launch Mode, so it gets no snapshot — add the city to Launch Mode, or clear the city for fleet-wide.'}
                    {' '}"Recompute now" builds one immediately (heavy; at most once every 10 minutes).
                </Alert>
            )}

            {truth?.ghost && (
                <Alert severity={truth.ghost.found > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                    Fleet-wide: ghost cleanup ({truth.ghost.mode === 'fix' ? 'fixing' : 'report only'}, {new Date(truth.ghost.at).toLocaleString()}):{' '}
                    {truth.ghost.found} driver(s) marked online with no live app{truth.ghost.complete ? '' : ' (scan still in progress)'}.
                    {truth.ghost.mode === 'fix'
                        ? ` ${truth.ghost.flipped} set offline.`
                        : truth.ghost.found > 0 ? ' Set GHOST_RECONCILE_ENABLED = 1 to set them offline so nudges reach them again.' : ''}
                </Alert>
            )}

            {d && t && (
                <>
                    {/* Segment cards */}
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {d.totalDrivers.toLocaleString()} drivers{trendLine}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 1 }}>
                        {SEGMENTS.map((s) => {
                            const delta = fmtSegmentDelta(trend?.segments?.[s.key]);
                            return (
                                <Box key={s.key} data-testid={`segment-${s.key}`}
                                    sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderLeft: `4px solid ${s.color}`, borderRadius: 2 }}>
                                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>{s.label}</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                        <Typography variant="h5" sx={{ fontWeight: 800 }}>{(d.segments[s.key] ?? 0).toLocaleString()}</Typography>
                                        <Typography variant="caption" color="textSecondary">{pct(d.segments[s.key] ?? 0, d.totalDrivers)}</Typography>
                                        {delta && <Typography variant="caption" sx={{ fontWeight: 700 }} data-testid={`delta-${s.key}`}>{delta}</Typography>}
                                    </Box>
                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', lineHeight: 1.3 }}>{s.means(days)}</Typography>
                                </Box>
                            );
                        })}
                    </Box>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 3 }} data-testid="drivers-counts">
                        Counts: {city
                            ? `drivers whose current city is ${city} — or, when that is blank, their home city, then the city of their last location`
                            : 'every driver'} (deleted and suspended left out). The App Type selector is not applied.
                    </Typography>

                    {/* Ride requests by hour */}
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Ride requests by hour (IST) — last {d.unfilled.days} days</Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }} data-testid="unfilled-totals">
                        {t.requests} requests: {OUTCOME_PARTS.map((k) => ({ k, n: outcomePart(t, k) }))
                            .filter(({ k, n }) => n > 0 || (k !== 'unrecorded' && k !== 'pending'))
                            .map(({ k, n }) => `${n} ${outcomeLabel(k, sec)}`).join(', ')}
                        {' '}— {pct(unfilledOf(t), t.requests)} unfilled (expired + cancelled after {sec} s).
                    </Typography>
                    {!hasSplit && (
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                            This snapshot predates the {sec} s split — every cancel is shown as "after {sec} s". Recompute for the split.
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 120, borderBottom: '1px solid', borderColor: 'divider' }}>
                        {d.unfilled.byHour.map((h) => (
                            <Box
                                key={h.hour} data-testid="unfilled-hour" data-hour={h.hour} data-requests={h.requests}
                                data-accepted={h.accepted} data-expired={h.expired}
                                data-cancelled-late={outcomePart(h, 'cancelledLate')} data-cancelled-early={outcomePart(h, 'cancelledEarly')}
                                data-unrecorded={outcomePart(h, 'unrecorded')} data-pending={h.pending}
                                title={`${hourLabel(h.hour)} IST: ${h.requests} requests — ${OUTCOME_PARTS.filter((k) => outcomePart(h, k) > 0)
                                    .map((k) => `${outcomePart(h, k)} ${outcomeLabel(k, sec)}`).join(', ') || 'none'}`}
                                sx={{ flex: 1, height: `${(h.requests / maxHour) * 100}%`, display: 'flex', flexDirection: 'column-reverse', minWidth: 0 }}
                            >
                                {OUTCOME_PARTS.map((k) => (outcomePart(h, k) > 0 ? (
                                    <Box key={k} sx={{ flexGrow: outcomePart(h, k), bgcolor: OUTCOME_COLORS[k] }} />
                                ) : null))}
                            </Box>
                        ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: '2px', mb: 1 }}>
                        {d.unfilled.byHour.map((h) => (
                            <Typography key={h.hour} variant="caption" color="textSecondary" sx={{ flex: 1, textAlign: 'center', fontSize: 10, minWidth: 0 }}>
                                {h.hour % 3 === 0 ? h.hour : ''}
                            </Typography>
                        ))}
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
                        {OUTCOME_PARTS.map((k) => (
                            <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: OUTCOME_COLORS[k] }} />
                                <Typography variant="caption">{outcomeLabel(k, sec)}</Typography>
                            </Box>
                        ))}
                    </Stack>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }} data-testid="requests-counts">
                        Counts: {city ? `ride requests whose pickup is in ${city}` : 'every ride request'} (the pickup city recorded at dispatch).
                        {' '}"Outcome not recorded" = still marked searching {d.unfilled.unrecordedAfterMin ?? 60}+ minutes after the request.
                    </Typography>

                    {/* By pickup zone */}
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Ride requests by pickup zone</Typography>
                    {d.unfilled.byZone.length === 0 ? (
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>No ride requests in this window.</Typography>
                    ) : (
                        <Box sx={{ overflowX: 'auto', mb: 3 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Zone</TableCell>
                                        <TableCell align="right">Requests</TableCell>
                                        <TableCell align="right">Accepted</TableCell>
                                        <TableCell align="right">Expired</TableCell>
                                        <TableCell align="right">Cancelled after {sec} s</TableCell>
                                        <TableCell align="right">Cancelled within {sec} s</TableCell>
                                        <TableCell align="right">Not recorded</TableCell>
                                        <TableCell align="right">Unfilled</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {d.unfilled.byZone.map((z) => (
                                        <TableRow key={z.zone}>
                                            <TableCell>{z.zone}</TableCell>
                                            <TableCell align="right">{z.requests}</TableCell>
                                            <TableCell align="right">{z.accepted}</TableCell>
                                            <TableCell align="right">{z.expired}</TableCell>
                                            <TableCell align="right">{outcomePart(z, 'cancelledLate')}</TableCell>
                                            <TableCell align="right">{outcomePart(z, 'cancelledEarly')}</TableCell>
                                            <TableCell align="right">{outcomePart(z, 'unrecorded')}</TableCell>
                                            <TableCell align="right"><strong>{pct(unfilledOf(z), z.requests)}</strong></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}

                    {/* Phone health */}
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Phone health</Typography>
                    {d.health.reports === 0 ? (
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            No phone-health reports yet — needs PHONE_HEALTH_ENABLED = 1 and the driver-app build that sends them.
                        </Typography>
                    ) : (
                        <>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                {d.health.reports} phones reported in the last 30 days; {d.health.withProblem} have at least one problem.
                            </Typography>
                            <Box sx={{ overflowX: 'auto', mb: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Problem</TableCell>
                                            <TableCell align="right">Drivers</TableCell>
                                            <TableCell>What it means</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {problems.map((p) => (
                                            <TableRow key={p.key} data-testid={`health-${p.key}`}>
                                                <TableCell>{p.label}</TableCell>
                                                <TableCell align="right">
                                                    {p.notMeasured
                                                        ? <Typography variant="caption" color="textSecondary">not measured in this app release</Typography>
                                                        : <strong>{p.count}</strong>}
                                                </TableCell>
                                                <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>{p.means}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>By phone maker</Typography>
                            <Box sx={{ overflowX: 'auto', mb: 3 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Maker</TableCell>
                                            <TableCell align="right">Reports</TableCell>
                                            <TableCell align="right">With a problem</TableCell>
                                            <TableCell align="right">Share</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {d.health.byManufacturer.map((m) => (
                                            <TableRow key={m.name}>
                                                <TableCell sx={{ textTransform: 'capitalize' }}>{m.name}</TableCell>
                                                <TableCell align="right">{m.reports}</TableCell>
                                                <TableCell align="right">{m.withProblem}</TableCell>
                                                <TableCell align="right">{pct(m.withProblem, m.reports)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </>
                    )}

                    {/* Still uninstalled, by removal week */}
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Still uninstalled — by the week the app was removed (last 8 weeks)</Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                        Only drivers who are still uninstalled: a reinstall clears the record. A removal is noticed when a push finds
                        the token dead, so it shows up only after we next push that driver.
                    </Typography>
                    {d.uninstallsByWeek.length === 0 ? (
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>None recorded yet.</Typography>
                    ) : (
                        <Box sx={{ mb: 2 }}>
                            {d.uninstallsByWeek.map((w) => (
                                <Box key={w.weekStart} data-testid="uninstall-week" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Typography variant="caption" sx={{ width: 96, flexShrink: 0 }}>Week of {weekLabel(w.weekStart)}</Typography>
                                    <Box sx={{ flex: 1, height: 10, bgcolor: 'action.hover', borderRadius: 1 }}>
                                        <Box sx={{ width: `${(w.count / maxWeek) * 100}%`, height: '100%', bgcolor: '#d32f2f', borderRadius: 1 }} />
                                    </Box>
                                    <Typography variant="caption" sx={{ width: 40, textAlign: 'right' }}>{w.count}</Typography>
                                </Box>
                            ))}
                        </Box>
                    )}

                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                        Push receipts count from driver build {d.config.ackMinVersion || '— (none yet: set PUSH_ACK_MIN_APP_VERSION)'}; location pings
                        from build {d.config.pingMinVersion || '— (off)'}. "Reachable" window: {days} days (REACH_ACTIVE_DAYS).
                    </Typography>
                </>
            )}
        </Paper>
    );
};

export default ReachTruthPanel;
